import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { CHAINS, getExplorerAddressUrl } from '../../services/api';
import { Icons } from './DashboardLayout';
import ActivityFeed from './ActivityFeed';

// Autonomy level config
const AUTONOMY_LEVELS = {
  1: { label: 'Manual', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', desc: 'Manual approval for every transaction' },
  2: { label: 'Semi-Auto', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', desc: 'Auto-approve under spending limits' },
  3: { label: 'Autonomous', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', desc: 'Full auto within strategy parameters' },
  4: { label: 'Sovereign', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', desc: 'Fully autonomous. No limits.' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'text-green-400', bg: 'bg-green-500/10' },
  paused: { label: 'Paused', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  frozen: { label: 'Frozen', color: 'text-red-400', bg: 'bg-red-500/10' },
};

const CHAIN_OPTIONS = [
  { id: 'base', name: 'Robinhood Network', logo: CHAINS.base.logo },
  { id: 'ethereum', name: 'Ethereum', logo: CHAINS.ethereum.logo },
  { id: 'arbitrum', name: 'Arbitrum', logo: CHAINS.arbitrum.logo },
  { id: 'polygon', name: 'Polygon', logo: CHAINS.polygon.logo },
  { id: 'optimism', name: 'Optimism', logo: CHAINS.optimism.logo },
];

export default function AgentsPage() {
  const { agents, token, refreshAgents, createAgent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(null);

  const handleAgentCreated = (agent, wallet, apiKey) => {
    setShowCreateModal(false);
    setShowApiKeyModal({ agent, wallet, apiKey });
    refreshAgents();
  };

  if (agents.length === 0 && !loading) {
    return (
      <div className="space-y-6">
        <EmptyState onCreateClick={() => setShowCreateModal(true)} />
        
        <AnimatePresence>
          {showCreateModal && (
            <CreateAgentModal 
              onClose={() => setShowCreateModal(false)}
              onCreated={handleAgentCreated}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showApiKeyModal && (
            <ApiKeyModal 
              agent={showApiKeyModal.agent}
              wallet={showApiKeyModal.wallet}
              apiKey={showApiKeyModal.apiKey}
              onClose={() => setShowApiKeyModal(null)}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-white/50 text-sm">Manage your AI agents and their configurations</p>
        <motion.button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-black font-medium text-sm"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="create-agent-btn"
        >
          <Icons.Plus size={16} />
          Create Agent
        </motion.button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agents Grid - 2 columns */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[200px] rounded-2xl bg-white/[0.02] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {agents.map((agent, index) => (
                <AgentCard key={agent.id} agent={agent} index={index} />
              ))}
            </div>
          )}
        </div>

        {/* Activity Feed - 1 column */}
        <div className="lg:col-span-1">
          <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] sticky top-4">
            <ActivityFeed limit={8} />
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateAgentModal 
            onClose={() => setShowCreateModal(false)}
            onCreated={handleAgentCreated}
          />
        )}
      </AnimatePresence>

      {/* API Key Modal */}
      <AnimatePresence>
        {showApiKeyModal && (
          <ApiKeyModal 
            agent={showApiKeyModal.agent}
            wallet={showApiKeyModal.wallet}
            apiKey={showApiKeyModal.apiKey}
            onClose={() => setShowApiKeyModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Agent Card Component
function AgentCard({ agent, index }) {
  const autonomy = AUTONOMY_LEVELS[agent.autonomyLevel] || AUTONOMY_LEVELS[2];
  const status = STATUS_CONFIG[agent.status] || STATUS_CONFIG.active;
  const wallets = agent.wallets || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/dashboard/agents/${agent.id}`}
        className="block relative p-5 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.06] hover:border-white/[0.14] transition-all group overflow-hidden"
        data-testid={`agent-card-${agent.id}`}
      >
        {/* Hover glow */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ 
            background: `radial-gradient(circle at 100% 0%, ${autonomy.color.includes('blue') ? 'rgba(59,130,246,0.08)' : autonomy.color.includes('green') ? 'rgba(34,197,94,0.08)' : autonomy.color.includes('yellow') ? 'rgba(234,179,8,0.08)' : 'rgba(239,68,68,0.08)'} 0%, transparent 50%)` 
          }}
        />
        
        {/* Header */}
        <div className="relative flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold truncate group-hover:text-white">{agent.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-[11px] text-white/40 font-mono">{agent.id?.slice(0, 16)}...</code>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(agent.id);
                }}
                className="text-white/20 hover:text-white/50 transition-colors"
              >
                <Icons.Copy size={12} />
              </button>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-lg text-[10px] font-medium ${status.color} ${status.bg}`}>
            {status.label}
          </span>
        </div>

        {/* Autonomy Badge */}
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${autonomy.color} ${autonomy.bg} border ${autonomy.border} mb-4`}>
          <span>L{agent.autonomyLevel}</span>
          <span className="text-white/40">·</span>
          <span>{autonomy.label}</span>
        </div>

        {/* Stats */}
        <div className="relative grid grid-cols-2 gap-3 pt-4 border-t border-white/[0.06]">
          <div>
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">Wallets</p>
            <p className="text-white font-semibold">{wallets.length}</p>
          </div>
          <div>
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">Chain</p>
            <div className="flex items-center gap-1.5">
              {wallets[0]?.chain && CHAINS[wallets[0].chain] && (
                <>
                  <img src={CHAINS[wallets[0].chain].logo} alt="" className="w-4 h-4" />
                  <span className="text-white text-sm">{CHAINS[wallets[0].chain].name}</span>
                </>
              )}
              {!wallets[0] && <span className="text-white/40 text-sm">-</span>}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

// Empty State
function EmptyState({ onCreateClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 px-4"
    >
      <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
        <Icons.Agents size={32} className="text-white/30" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Create Your First Agent</h3>
      <p className="text-white/50 text-center max-w-md mb-8">
        Agents are autonomous wallets that can execute DeFi operations on your behalf. Create one to get started.
      </p>
      <motion.button
        onClick={onCreateClick}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        data-testid="create-first-agent-btn"
      >
        <Icons.Plus size={18} />
        Create Agent
      </motion.button>
    </motion.div>
  );
}

// Create Agent Modal
function CreateAgentModal({ onClose, onCreated }) {
  const { createAgent } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [autonomyLevel, setAutonomyLevel] = useState(2);
  const [primaryChain, setPrimaryChain] = useState('base');
  const [maxPerTx, setMaxPerTx] = useState('0.1');
  const [dailyLimit, setDailyLimit] = useState('1');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Please enter an agent name');
      return;
    }
    setCreating(true);
    setError('');
    
    const agentData = {
      name,
      description,
      autonomyLevel,
      chain: primaryChain,
      spendingLimits: {
        maxPerTx: (parseFloat(maxPerTx) * 1e18).toString(),
        dailyLimit: (parseFloat(dailyLimit) * 1e18).toString(),
        requireApproval: autonomyLevel <= 2
      }
    };
    
    const result = await createAgent(agentData);
    
    if (result.success) {
      onCreated(result.agent, result.wallet, result.apiKey);
    } else {
      setError(result.error || 'Failed to create agent');
    }
    setCreating(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Create Agent</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 max-h-[65vh] overflow-y-auto">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Agent Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., TradingBot"
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white placeholder:text-white/30 outline-none focus:border-white/20 transition-colors"
              data-testid="agent-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., DCA into blue chips"
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white placeholder:text-white/30 outline-none focus:border-white/20 transition-colors"
            />
          </div>

          {/* Autonomy Level */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Autonomy Level</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(AUTONOMY_LEVELS).map(([level, config]) => (
                <button
                  key={level}
                  onClick={() => setAutonomyLevel(parseInt(level))}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    autonomyLevel === parseInt(level)
                      ? `${config.bg} ${config.border} ${config.color}`
                      : 'border-white/[0.06] text-white/50 hover:border-white/10'
                  }`}
                >
                  <div className="font-medium text-sm">L{level} — {config.label}</div>
                  <div className="text-[11px] text-white/40 mt-1 line-clamp-2">{config.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Primary Chain */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Primary Chain</label>
            <div className="flex flex-wrap gap-2">
              {CHAIN_OPTIONS.map((chain) => (
                <button
                  key={chain.id}
                  onClick={() => setPrimaryChain(chain.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all ${
                    primaryChain === chain.id
                      ? 'bg-white/[0.08] border-white/20 text-white'
                      : 'border-white/[0.06] text-white/50 hover:border-white/10'
                  }`}
                >
                  <img src={chain.logo} alt={chain.name} className="w-4 h-4 object-contain" />
                  {chain.name}
                </button>
              ))}
            </div>
          </div>

          {/* Spending Limits */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <label className="block text-sm font-medium text-white/70 mb-3">Spending Limits</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-white/40 mb-1">Max per transaction (ETH)</label>
                <input
                  type="number"
                  step="0.01"
                  value={maxPerTx}
                  onChange={(e) => setMaxPerTx(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-black border border-white/10 text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">Daily limit (ETH)</label>
                <input
                  type="number"
                  step="0.1"
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-black border border-white/10 text-white text-sm outline-none"
                />
              </div>
            </div>
            <p className="text-white/30 text-xs mt-2">
              {autonomyLevel <= 2 ? 'Transactions above limits will require your approval.' : 'Agent will auto-execute within these limits.'}
            </p>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <motion.button
            onClick={handleCreate}
            disabled={creating}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="create-agent-submit"
          >
            {creating ? 'Creating...' : 'Create Agent'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// API Key Modal - Shows once after agent creation
function ApiKeyModal({ agent, wallet, apiKey, onClose }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const data = {
      agentId: agent?.id,
      agentName: agent?.name,
      walletAddress: wallet?.address,
      chain: wallet?.chain,
      apiKey: apiKey,
      createdAt: new Date().toISOString(),
      warning: 'Keep this API key secret. It cannot be retrieved again.'
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aibl-agent-${agent?.name?.toLowerCase().replace(/\s+/g, '-') || 'new'}-credentials.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-yellow-500/20 bg-yellow-500/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-400">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Save Your API Key</h2>
              <p className="text-yellow-400/80 text-sm">This key will only be shown once</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Agent Info */}
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Agent Created</p>
            <p className="text-white font-medium">{agent?.name}</p>
            {wallet?.address && (
              <a 
                href={getExplorerAddressUrl(wallet.chain, wallet.address)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 text-sm hover:underline mt-1 block"
              >
                {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
              </a>
            )}
          </div>

          {/* API Key */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">API Key</p>
            <div className="p-4 rounded-xl bg-black border border-white/[0.08] font-mono text-sm break-all">
              <code className="text-green-400">{apiKey}</code>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <motion.button
              onClick={handleCopy}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white text-sm hover:bg-white/[0.08] transition-colors"
              whileTap={{ scale: 0.98 }}
            >
              {copied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <Icons.Copy size={16} />
                  Copy Key
                </>
              )}
            </motion.button>
            <motion.button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white text-sm hover:bg-white/[0.08] transition-colors"
              whileTap={{ scale: 0.98 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Download
            </motion.button>
          </div>

          {/* Warning */}
          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 text-sm">
              This API key cannot be retrieved later. Make sure to save it securely before closing this window.
            </p>
          </div>

          {/* Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-black text-white focus:ring-white/20"
            />
            <span className="text-white/70 text-sm">
              I have saved my API key and understand it will not be shown again
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06]">
          <motion.button
            onClick={onClose}
            disabled={!acknowledged}
            className="w-full px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-30 disabled:cursor-not-allowed"
            whileHover={acknowledged ? { scale: 1.02 } : {}}
            whileTap={acknowledged ? { scale: 0.98 } : {}}
          >
            Continue to Dashboard
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
